'use client'

import { Icon } from '@/components/ui/icon'
import { CONTACT, CONTACT_PATHS } from '@/config/contact'
import type { IconName } from '@/config/icons'
import { navigationItems } from '@/config/nav/main-navigation'
import { useCurrentUser } from '@/hooks/use-current-user'
import { cn } from '@heroui/react'
import { signIn } from 'next-auth/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { closeMobileMenu, useMobileMenuOpen } from './mobile-menu-store'

/**
 * The site menu for phones and tablets.
 *
 * Phones (below md) already have Home, Services, Gallery, Book and Quote in
 * the bottom bar, so the menu is a short bottom sheet above that bar with
 * only the sections the bar lacks (Wraps, Fleet, Colours, Articles, Contact,
 * Products when the shop is on), a Call button and Sign in. Repeating the
 * bar's items here made the sheet a second navigation competing with the
 * first.
 *
 * Tablets (md to lg) have no bottom bar, so the same component is a right
 * side panel under the header listing every section plus the quote CTA.
 * The shell (fixed, pinned to the header's live bottom edge and the bar's
 * top) is the one CartDrawerV2 uses.
 *
 * Triggers: the hamburger in AppHeader (md to lg) and the Menu tab in
 * BottomNav (below md). Both call openMobileMenu().
 */

/** Sections the phone bottom bar already covers; hidden from the sheet below md. */
const IN_PHONE_BAR = new Set(['/', '/services', '/gallery'])
export function MobileMenuDrawer({ ecommerceEnabled = false }: { ecommerceEnabled?: boolean }) {
  const isOpen = useMobileMenuOpen()
  const pathname = usePathname()
  const user = useCurrentUser()
  const closeRef = useRef<HTMLButtonElement>(null)

  // Route change closes the menu (a tapped link navigates; the drawer must
  // not sit over the new page).
  useEffect(() => {
    closeMobileMenu()
  }, [pathname])

  // Escape closes; body scroll locks while open; focus lands on the close
  // control so keyboard and screen-reader users start inside the dialog.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileMenu()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [isOpen])

  // Home is the logo (and the phone bar's first tab); Products only when the shop is on.
  const items = navigationItems.filter(
    (item) => item.href !== '/' && (ecommerceEnabled || item.href !== '/products')
  )

  const isActive = (href: string) => (href === '/' ? pathname === '/' : pathname.startsWith(href))

  return (
    <div
      className='lg:hidden'
      style={
        {
          '--menu-top': 'var(--app-header-visible-h, var(--app-header-h, 64px))',
          // The bar scroll-hides by sliding down --app-bottom-nav-offset px
          // (BottomNav publishes it on <html>); the sheet is pinned to the
          // bar's live top edge so it rides off screen with the bar rather
          // than hanging above the gap. Negative when the bar is hidden.
          '--menu-bottom': 'calc(var(--app-bottom-nav-h, 0px) - var(--app-bottom-nav-offset, 0px))',
        } as React.CSSProperties
      }
    >
      <div
        className={cn(
          'fixed left-0 right-0 top-[var(--menu-top)] bottom-[var(--menu-bottom)] z-[990] bg-black/50 transition-opacity duration-300 md:z-[1050]',
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={closeMobileMenu}
        aria-hidden='true'
      />

      {/* Phone: bottom sheet (auto height, rounded top, slides up). It sits
          BELOW the bottom bar in stacking order (z 990 vs the bar's 1000), so
          it rises from behind the bar and drops back behind it; the bar
          never gets covered. Tablet: right panel (full height under the
          header, slides in), above everything but the header.

          Closed, the sheet clears the viewport on its own: translating by its
          own height alone left it standing on --menu-bottom, so its top strip
          showed whenever nothing was drawn over that band. Adding
          --menu-bottom to the travel lands its top edge exactly on the
          viewport bottom, and `invisible` (transitioned, so the slide-out
          still plays to the end) keeps it off screen even if the variable is
          ever wrong. */}
      <div
        id='site-menu'
        className={cn(
          'fixed right-0 z-[990] flex flex-col bg-background shadow-xl duration-300 ease-in-out md:z-[1050]',
          'transition-[transform,translate,visibility]',
          'left-0 bottom-[var(--menu-bottom)] rounded-t-2xl',
          'md:left-auto md:top-[var(--menu-top)] md:w-full md:max-w-[384px] md:rounded-none',
          isOpen
            ? 'visible translate-y-0 md:translate-x-0'
            : 'invisible pointer-events-none translate-y-[calc(100%_+_var(--menu-bottom))] md:translate-y-0 md:translate-x-full'
        )}
        role='dialog'
        aria-modal='true'
        aria-label='Site menu'
        aria-hidden={!isOpen}
      >
        <div className='flex items-center justify-between px-4 pt-3 pb-1 md:border-b md:border-border md:py-3'>
          <h2 className='cw-ui-heading text-lg text-foreground'>Menu</h2>
          <button
            ref={closeRef}
            type='button'
            onClick={closeMobileMenu}
            className='flex h-11 w-11 items-center justify-center rounded-full text-muted hover:bg-overlay hover:text-foreground'
            aria-label='Close menu'
            tabIndex={isOpen ? 0 : -1}
          >
            <Icon icon='cw-close' className='h-6 w-6' />
          </button>
        </div>

        <nav aria-label='Site sections' className='overflow-y-auto px-2 pb-2 md:flex-1 md:py-2'>
          {items.map((item) => (
            <MenuLink
              key={item.href}
              href={item.href}
              icon={item.icon}
              label={item.label}
              active={isActive(item.href)}
              open={isOpen}
              phoneHidden={IN_PHONE_BAR.has(item.href)}
            />
          ))}
        </nav>

        <div
          className='flex items-center justify-between gap-3 border-t border-border px-4 pt-3 md:flex-col md:items-stretch md:pt-4'
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
        >
          {/* Quote: tablet only; the phone bar has its own Quote tab. */}
          <Link
            href={CONTACT_PATHS.quote}
            tabIndex={isOpen ? 0 : -1}
            className='hidden h-12 items-center justify-center gap-2 rounded-full bg-[var(--btn-primary-bg)] px-6 text-sm font-semibold text-[var(--btn-primary-fg)] no-underline transition-colors hover:bg-[var(--btn-primary-bg-hover)] md:inline-flex'
          >
            <Icon icon='quote' className='h-5 w-5' />
            Get a free quote
          </Link>
          <a
            href={`tel:${CONTACT.phoneTel}`}
            tabIndex={isOpen ? 0 : -1}
            className='inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-full border border-border px-5 text-sm font-semibold text-foreground no-underline transition-colors hover:bg-overlay md:h-12 md:flex-none'
          >
            <Icon icon='phone' className='h-5 w-5' />
            Call {CONTACT.phoneDisplay}
          </a>
          {user ? (
            <Link
              href='/account'
              tabIndex={isOpen ? 0 : -1}
              className='inline-flex h-11 items-center justify-center gap-2 px-2 text-sm font-medium text-muted no-underline hover:text-foreground'
            >
              <Icon icon='user' className='h-5 w-5' />
              Account
            </Link>
          ) : (
            <button
              type='button'
              tabIndex={isOpen ? 0 : -1}
              onClick={() => signIn(undefined, { callbackUrl: pathname })}
              className='inline-flex h-11 items-center justify-center gap-2 px-2 text-sm font-medium text-muted hover:text-foreground'
            >
              <Icon icon='sign-in' className='h-5 w-5' />
              Sign in
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function MenuLink({
  href,
  icon,
  label,
  active,
  open,
  phoneHidden,
}: {
  href: string
  icon: IconName
  label: string
  active: boolean
  open: boolean
  phoneHidden: boolean
}) {
  return (
    <Link
      href={href}
      aria-current={active ? 'page' : undefined}
      tabIndex={open ? 0 : -1}
      className={cn(
        'flex min-h-[48px] items-center gap-4 rounded-xl px-4 py-3 no-underline transition-colors',
        phoneHidden ? 'hidden md:flex' : 'flex',
        active ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-overlay'
      )}
    >
      <Icon icon={icon} className={cn('h-5 w-5', active ? 'text-accent-foreground' : 'text-muted')} />
      <span className='text-base font-medium'>{label}</span>
    </Link>
  )
}
